import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { AlertCircle } from 'lucide-react';

const CustomFieldsSection = ({ customFieldDefinitions }) => {
    const { register, control, formState: { errors } } = useFormContext();

    if (!customFieldDefinitions || customFieldDefinitions.length === 0) {
        return null;
    }

    // Group definitions by category
    const groupedFields = customFieldDefinitions.reduce((acc, field) => {
        const category = field.category || 'Other';
        if (!acc[category]) acc[category] = [];
        acc[category].push(field);
        return acc;
    }, {});

    const renderFieldInput = (field, index) => {
        // We use a hidden input for the field_id to ensure it's submitted
        // The value input depends on the type
        const fieldName = `custom_fields.${index}.value`;

        // Error handling helper
        const fieldError = errors?.custom_fields?.[index]?.value || errors?.custom_fields?.[field.field_id];
        const errorMessage = fieldError?.message;

        switch (field.field_type) {
            case 'textarea':
                return (
                    <textarea
                        {...register(fieldName)}
                        rows={3}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder={field.placeholder}
                    />
                );
            case 'select':
                return (
                    <select
                        {...register(fieldName)}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    >
                        <option value="">{field.placeholder || 'Select an option'}</option>
                        {field.options?.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                );
            case 'checkbox':
                return (
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            {...register(fieldName)}
                            className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-500">{field.description}</span>
                    </div>
                );
            default: // text, number, decimal, email, phone, url, date
                return (
                    <input
                        type={mapHtmlType(field.field_type)}
                        {...register(fieldName, {
                            valueAsNumber: ['number', 'decimal'].includes(field.field_type)
                        })}
                        className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder={field.placeholder}
                    />
                );
        }
    };

    const mapHtmlType = (type) => {
        switch (type) {
            case 'number':
            case 'decimal': return 'number';
            case 'date': return 'date';
            case 'email': return 'email';
            case 'tel': return 'tel';
            case 'url': return 'url';
            default: return 'text';
        }
    };

    return (
        <>
            {Object.entries(groupedFields).map(([category, fields]) => (
                <div key={category} className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6 mb-6">
                    <div className="md:grid md:grid-cols-3 md:gap-6">
                        <div className="md:col-span-1">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">{category}</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Additional information.
                            </p>
                        </div>
                        <div className="mt-5 md:mt-0 md:col-span-2">
                            <div className="grid grid-cols-6 gap-6">
                                {fields.map((field, index) => {
                                    // IMPORTANT: The index here must match the index in the form array.
                                    // However, grouping messes up simple indexing. 
                                    // Ideally, the parent component should have mapped the definitions to the form array 
                                    // and we just render them. 
                                    // But to keep UI grouped by category, we need to find the correct index 
                                    // in the flat `custom_fields` array that corresponds to this `field_id`.

                                    // NOTE: This assumes `field.index` or similar is passed, or we need a way to look it up.
                                    // For now, simpler approach: The parent prepares the defaultValues with all custom fields 
                                    // pre-populated (even empty ones) in the correct order. 
                                    // We need to find the index of this definition in the original flat list.

                                    const globalIndex = customFieldDefinitions.findIndex(f => f.field_id === field.field_id);

                                    // Hidden input for field_id is needed to bind the array object correctly
                                    // <input type="hidden" {...register(`custom_fields.${globalIndex}.field_id`)} value={field.field_id} />
                                    // But react-hook-form doesn't like hardcoding values in register if not in defaultValues.
                                    // We assume defaultValues initialized correctly.

                                    const error = errors?.custom_fields?.[globalIndex]?.value || errors?.custom_fields?.[field.field_id];

                                    return (
                                        <div key={field.field_id} className="col-span-6 sm:col-span-4">
                                            <input
                                                type="hidden"
                                                {...register(`custom_fields.${globalIndex}.field_id`)}
                                            />
                                            <label className="block text-sm font-medium text-gray-700">
                                                {field.label} {field.is_required && <span className="text-red-500">*</span>}
                                            </label>
                                            <div className="mt-1">
                                                {renderFieldInput(field, globalIndex)}
                                            </div>
                                            {field.description && field.field_type !== 'checkbox' && (
                                                <p className="mt-1 text-xs text-gray-500">{field.description}</p>
                                            )}
                                            {error && (
                                                <div className="mt-1 flex items-center text-sm text-red-600">
                                                    <AlertCircle className="h-4 w-4 mr-1" />
                                                    {error.message}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </>
    );
};

export default CustomFieldsSection;
